import axios from '../api/axios';

const BASE = '/api/ee/clearance';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AgentBoxNumber {
  box_id: string;
  sub_box_id: string;
  label?: string;
  is_default: boolean;
}

export interface BrokerProfile {
  id?: number;
  org_id: number;
  agent_box_id?: string;
  agent_sub_box_id?: string;
  agent_box_numbers?: AgentBoxNumber[];
  broker_name_zh?: string;
  broker_name_en?: string;
  broker_tax_id?: string;
  broker_phone?: string;
  broker_email?: string;
  default_duty_method_code?: string;
  default_storage_code?: string;
  updated_at?: string;
}

export interface Customer {
  id: number;
  org_id: number;
  tax_id: string;
  short_code?: string;
  name_zh: string;
  name_en: string;
  address_zh?: string;
  address_en?: string;
  customs_monitor_code?: string;
  aeo_code?: string;
  bonded_factory_code?: string;
  default_declaration_type: string;
  default_storage_code?: string;
  default_trade_condition?: string;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  parse_profile_sample_count: number;
  inference_mode: string;        // claude / local / hybrid
  local_model_name?: string;
  local_model_accuracy?: number; // 0–100
  training_notes?: string;       // 人工指示 Claude 的訓練備忘
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Vendor {
  id: number;
  org_id: number;
  customer_id: number;
  name_en: string;
  name_zh?: string;
  country_code?: string;
  address_en?: string;
  contact_name?: string;
  contact_email?: string;
  notes?: string;
  is_active: boolean;
  created_at: string;
}

export interface VendorCreate {
  name_en: string;
  name_zh?: string;
  country_code?: string;
  address_en?: string;
  contact_name?: string;
  contact_email?: string;
  notes?: string;
}

export interface CustomerCreate {
  tax_id: string;
  short_code?: string;
  name_zh: string;
  name_en: string;
  address_zh?: string;
  address_en?: string;
  customs_monitor_code?: string;
  aeo_code?: string;
  bonded_factory_code?: string;
  default_declaration_type?: string;
  default_storage_code?: string;
  default_trade_condition?: string;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
}

export interface ClearanceCase {
  id: number;
  org_id: number;
  customer_id: number;
  vendor_id?: number;
  case_no: string;
  hawb_no?: string;
  mawb_no?: string;
  ref_no?: string;
  transport_mode: string;
  carrier_code?: string;
  origin_country?: string;
  etd?: string;
  eta?: string;
  arrival_date?: string;
  currency: string;
  exchange_rate?: string;
  exchange_rate_date?: string;
  status: string;
  filed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CaseCreate {
  customer_id: number;
  vendor_id?: number;
  case_no: string;
  hawb_no?: string;
  mawb_no?: string;
  ref_no?: string;
  transport_mode?: string;
  carrier_code?: string;
  origin_country?: string;
  etd?: string;
  eta?: string;
  arrival_date?: string;
  currency?: string;
  exchange_rate?: string;
  exchange_rate_date?: string;
}

export interface ClearanceDocument {
  id: number;
  case_id: number;
  doc_type: string;
  doc_format: string;
  file_name?: string;
  file_size?: number;
  page_count?: number;
  parse_status: string;
  parse_confidence?: number;
  parse_error?: string;
  parse_started_at?: string;
  parsed_at?: string;
  created_at: string;
}

export interface LineItem {
  seq: number;
  hs_code?: string;
  hs_stat?: string;
  hs_check?: string;
  description_en?: string;
  description_zh?: string;
  origin_country?: string;
  qty?: number;
  unit?: string;
  unit_price_usd?: number;
  amount_usd?: number;
  amount_twd?: number;
  gross_weight_kg?: number;
  net_weight_kg?: number;
  stat_qty?: number;
  stat_unit?: string;
  duty_rate?: number;
  tax_method_code?: string;
  notes?: string;
}

export interface Declaration {
  id: number;
  case_id: number;
  org_id: number;
  version: number;
  is_current: boolean;
  declaration_type?: string;
  import_export_code?: string;
  transport_mode?: string;
  carrier_code?: string;
  hawb_no?: string;
  mawb_no?: string;
  origin_country?: string;
  trade_condition?: string;
  currency?: string;
  exchange_rate?: string;
  total_packages?: number;
  gross_weight_kg?: string;
  net_weight_kg?: string;
  invoice_amount?: string;
  freight_amount?: string;
  insurance_amount?: string;
  cif_amount_twd?: number;
  importer_tax_id?: string;
  importer_name_zh?: string;
  importer_name_en?: string;
  importer_address_en?: string;
  customs_monitor_code?: string;
  aeo_code?: string;
  agent_box_id?: string;
  agent_sub_box_id?: string;
  duty_method_code?: string;
  total_duty_amount?: number;
  line_items: LineItem[];
  review_status: string;
  reviewed_at?: string;
  review_notes?: string;
  xml_generated_at?: string;
  xml_schema_version?: string;
  filing_status: string;
  filing_ref_no?: string;
  filing_submitted_at?: string;
  created_at: string;
}

export interface SourceMap {
  case_id: number;
  declaration_id: number;
  version: number;
  source_mapping: Record<string, any>;
}

// ─── Broker Profile ───────────────────────────────────────────────────────────

export const getBrokerProfile = async (): Promise<BrokerProfile> => {
  const r = await axios.get<BrokerProfile>(`${BASE}/broker-profile`);
  return r.data;
};

export const updateBrokerProfile = async (data: Partial<BrokerProfile>): Promise<BrokerProfile> => {
  const r = await axios.put<BrokerProfile>(`${BASE}/broker-profile`, data);
  return r.data;
};

// ─── Customers ────────────────────────────────────────────────────────────────

export const getCustomers = async (q?: string): Promise<Customer[]> => {
  const r = await axios.get<Customer[]>(`${BASE}/customers`, { params: q ? { q } : undefined });
  return r.data;
};

export const createCustomer = async (data: CustomerCreate): Promise<Customer> => {
  const r = await axios.post<Customer>(`${BASE}/customers`, data);
  return r.data;
};

export const saveTrainingNotes = async (id: number, training_notes: string): Promise<Customer> => {
  const r = await axios.put<Customer>(`${BASE}/customers/${id}`, { training_notes });
  return r.data;
};

export const updateCustomer = async (id: number, data: Partial<CustomerCreate> & { is_active?: boolean }): Promise<Customer> => {
  const r = await axios.put<Customer>(`${BASE}/customers/${id}`, data);
  return r.data;
};

export const deleteCustomer = async (id: number): Promise<void> => {
  await axios.delete(`${BASE}/customers/${id}`);
};

// ─── Vendors ──────────────────────────────────────────────────────────────────

export const getVendors = async (customerId: number, q?: string): Promise<Vendor[]> => {
  const r = await axios.get<Vendor[]>(`${BASE}/customers/${customerId}/vendors`, { params: q ? { q } : undefined });
  return r.data;
};

export const createVendor = async (customerId: number, data: VendorCreate): Promise<Vendor> => {
  const r = await axios.post<Vendor>(`${BASE}/customers/${customerId}/vendors`, data);
  return r.data;
};

export const updateVendor = async (customerId: number, vendorId: number, data: Partial<VendorCreate>): Promise<Vendor> => {
  const r = await axios.put<Vendor>(`${BASE}/customers/${customerId}/vendors/${vendorId}`, data);
  return r.data;
};

export const deleteVendor = async (customerId: number, vendorId: number): Promise<void> => {
  await axios.delete(`${BASE}/customers/${customerId}/vendors/${vendorId}`);
};

// ─── Cases ────────────────────────────────────────────────────────────────────

export const getCases = async (params?: { status?: string; customer_id?: number; q?: string }): Promise<ClearanceCase[]> => {
  const r = await axios.get<ClearanceCase[]>(`${BASE}/cases`, { params });
  return r.data;
};

export const getCase = async (id: number): Promise<ClearanceCase> => {
  const r = await axios.get<ClearanceCase>(`${BASE}/cases/${id}`);
  return r.data;
};

export const createCase = async (data: CaseCreate): Promise<ClearanceCase> => {
  const r = await axios.post<ClearanceCase>(`${BASE}/cases`, data);
  return r.data;
};

export const updateCase = async (id: number, data: Partial<CaseCreate> & { status?: string }): Promise<ClearanceCase> => {
  const r = await axios.put<ClearanceCase>(`${BASE}/cases/${id}`, data);
  return r.data;
};

export const deleteCase = async (id: number): Promise<void> => {
  await axios.delete(`${BASE}/cases/${id}`);
};

// ─── Documents ────────────────────────────────────────────────────────────────

export const getDocuments = async (caseId: number): Promise<ClearanceDocument[]> => {
  const r = await axios.get<ClearanceDocument[]>(`${BASE}/cases/${caseId}/documents`);
  return r.data;
};

export const uploadDocument = async (caseId: number, file: File, docType?: string): Promise<ClearanceDocument[]> => {
  const form = new FormData();
  form.append('file', file);
  if (docType) form.append('doc_type', docType);
  const r = await axios.post<ClearanceDocument[]>(`${BASE}/cases/${caseId}/documents`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return r.data;
};

export const deleteDocument = async (caseId: number, docId: number): Promise<void> => {
  await axios.delete(`${BASE}/cases/${caseId}/documents/${docId}`);
};

export const parseDocuments = async (caseId: number): Promise<void> => {
  await axios.post(`${BASE}/cases/${caseId}/documents/parse`);
};

export const documentFileUrl = (caseId: number, docId: number): string =>
  `${BASE}/cases/${caseId}/documents/${docId}/file`;

// ─── Declaration ──────────────────────────────────────────────────────────────

export const generateDeclaration = async (caseId: number): Promise<Declaration> => {
  const r = await axios.post<Declaration>(`${BASE}/cases/${caseId}/declaration/generate`);
  return r.data;
};

export const getDeclaration = async (caseId: number): Promise<Declaration> => {
  const r = await axios.get<Declaration>(`${BASE}/cases/${caseId}/declaration`);
  return r.data;
};

export const updateDeclaration = async (caseId: number, data: Partial<Declaration>): Promise<Declaration> => {
  const r = await axios.put<Declaration>(`${BASE}/cases/${caseId}/declaration`, data);
  return r.data;
};

export const approveDeclaration = async (caseId: number): Promise<void> => {
  await axios.post(`${BASE}/cases/${caseId}/declaration/approve`);
};

export const submitDeclaration = async (caseId: number): Promise<any> => {
  const r = await axios.post(`${BASE}/cases/${caseId}/declaration/submit`);
  return r.data;
};

export const updateFilingResult = async (caseId: number, data: { filing_ref_no?: string; customs_response_xml?: string }): Promise<Declaration> => {
  const r = await axios.put<Declaration>(`${BASE}/cases/${caseId}/declaration/filing`, data);
  return r.data;
};

export const getDeclarationHistory = async (caseId: number): Promise<Declaration[]> => {
  const r = await axios.get<Declaration[]>(`${BASE}/cases/${caseId}/declaration/history`);
  return r.data;
};

export const getSourceMap = async (caseId: number): Promise<SourceMap> => {
  const r = await axios.get<SourceMap>(`${BASE}/cases/${caseId}/declaration/source-map`);
  return r.data;
};

export const generateXml = async (caseId: number): Promise<{ message: string; size: number; schema_version: string }> => {
  const r = await axios.post(`${BASE}/cases/${caseId}/declaration/generate-xml`);
  return r.data;
};

export const downloadXmlUrl = (caseId: number) => `${BASE}/cases/${caseId}/declaration/xml`;
export const printHtmlUrl = (caseId: number) => `${BASE}/cases/${caseId}/declaration/print-html`;
export const downloadPdfUrl = (caseId: number) => `${BASE}/cases/${caseId}/declaration/download-pdf`;

// ─── Training / Knowledge Distillation ───────────────────────────────────────

export interface ParseProfile {
  customer_id: number;
  customer_name: string;
  parse_profile: Record<string, any> | null;
  parse_profile_sample_count: number;
  parse_profile_updated_at: string | null;
}

export const analyzeTrainingPair = async (caseId: number): Promise<{ message: string; customer_id: number }> => {
  const r = await axios.post(`${BASE}/cases/${caseId}/training/analyze`);
  return r.data;
};

export type DiffReasonType = 'transform_rule' | 'source_mapping' | 'format_rule' | 'manual_fill' | 'correction' | 'ai_error' | '';

export interface DiffAnnotation {
  item_sno: number;
  field: string;
  reason_type: DiffReasonType;
  explanation: string;
}

export interface FieldDiff {
  item_sno: number;
  field: string;
  generated: string;
  reference: string;
  diff_type: 'content_diff' | 'missing' | 'extra';
}

export interface ItemComparison {
  sno: number;
  inv_description: string;
  inv_extra: Record<string, string>;
  ai_description: string;
  actual_description: string;
  match: boolean | null;
}

export interface TrainingResult {
  sample_id: number;
  case_id: number;
  customer_id: number;
  ai_summary: string;
  field_diffs: FieldDiff[];
  diff_annotations?: DiffAnnotation[];
  item_comparison?: ItemComparison[];
  stats: {
    total: number;
    match: number;
    diff: number;
    missing: number;
    extra: number;
    accuracy_pct: number;
    human_only_excluded?: number;
  };
  customer_sample_count: number;
  is_verified: boolean;
  analyzed_at: string;
}

export const getTrainingResult = async (caseId: number): Promise<TrainingResult> => {
  const r = await axios.get<TrainingResult>(`${BASE}/cases/${caseId}/training/result`);
  return r.data;
};

export interface TrainingSampleSummary {
  id: number;
  case_id: number;
  customer_id: number;
  is_verified: boolean;
  has_reference: boolean;
  field_diffs_count: number;
  created_at: string;
}

export const listTrainingSamples = async (params?: { customer_id?: number; verified_only?: boolean }): Promise<TrainingSampleSummary[]> => {
  const r = await axios.get<TrainingSampleSummary[]>(`${BASE}/training-samples`, { params });
  return r.data;
};

export const verifyTrainingSample = async (sampleId: number): Promise<void> => {
  await axios.put(`${BASE}/training-samples/${sampleId}/verify`);
};

export const saveDiffAnnotations = async (sampleId: number, annotations: DiffAnnotation[]): Promise<void> => {
  await axios.put(`${BASE}/training-samples/${sampleId}/annotations`, annotations);
};

export const getCustomerParseProfile = async (customerId: number): Promise<ParseProfile> => {
  const r = await axios.get<ParseProfile>(`${BASE}/customers/${customerId}/parse-profile`);
  return r.data;
};

export interface DescriptionRuleComponent {
  field: string;
  prefix: string;
  enabled: boolean;
}

export interface ItemDescriptionRule {
  components: DescriptionRuleComponent[];
  separator: string;
  example_item?: Record<string, string>;
  example_output?: string;
}

export const saveItemDescriptionRule = async (customerId: number, rule: ItemDescriptionRule): Promise<void> => {
  await axios.put(`${BASE}/customers/${customerId}/parse-profile/item-description-rule`, rule);
};

export const trainingExportUrl = (customerId?: number, verifiedOnly = true, docType = 'commercial_invoice') => {
  const params = new URLSearchParams({ verified_only: String(verifiedOnly), doc_type: docType });
  if (customerId) params.set('customer_id', String(customerId));
  return `${BASE}/training-samples/export?${params}`;
};

// ─── Training Pool ────────────────────────────────────────────────────────────

export interface TrainingPoolFilePreview {
  filename: string;
  doc_type: string;
  doc_type_label: string;
  type_confidence: number;
  customer_id: number | null;
  customer_name: string | null;
  customer_confidence: number;
  file_size: number;
}

export interface TrainingPoolCustomerOption {
  id: number;
  name_zh: string;
  name_en: string;
}

export interface TrainingPoolPreviewResponse {
  files: TrainingPoolFilePreview[];
  suggested_customer_id: number | null;
  has_source: boolean;
  has_reference: boolean;
  customers: TrainingPoolCustomerOption[];
}

export const previewTrainingPool = async (files: File[]): Promise<TrainingPoolPreviewResponse> => {
  const form = new FormData();
  files.forEach(f => form.append('files', f));
  const r = await axios.post<TrainingPoolPreviewResponse>(`${BASE}/training-pool/preview`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return r.data;
};

export interface TrainingPoolSubmitResponse {
  message: string;
  batch_id: string;
  customer: string;
  files: number;
}

// ─── NAS Inbox ────────────────────────────────────────────────────────────────

export interface NasTicketFile {
  filename: string;
  doc_type: string;
  doc_type_label: string;
  type_confidence: number;
}

export interface NasTicket {
  folder: string;
  file_count: number;
  size_bytes: number;
  is_trained: boolean;
  files: NasTicketFile[];
  suggested_customer_id: number | null;
  suggested_customer_name: string | null;
  extracted_company_name: string | null;
  has_source: boolean;
  has_reference: boolean;
}

export interface NasInboxResponse {
  enabled: boolean;
  customers?: TrainingPoolCustomerOption[];
  tickets?: NasTicket[];
}

export const listNasInbox = async (includeTrained = false): Promise<NasInboxResponse> => {
  const r = await axios.get<NasInboxResponse>(`${BASE}/training-pool/nas-inbox`, {
    params: includeTrained ? { include_trained: true } : undefined,
  });
  return r.data;
};

export interface NasSubmitItem {
  folder: string;
  customer_id?: number | null;
  auto_create_name?: string | null;
  file_types: Record<string, string>;
}

export interface NasSubmitResponse {
  submitted: number;
  items: { folder: string; batch_id: string; customer: string }[];
}

// ─── Training Advisor ─────────────────────────────────────────────────────────

export interface AdvisorMessage {
  role: 'user' | 'assistant';
  content: string;
}

export const chatWithAdvisor = async (
  messages: AdvisorMessage[],
  customerId?: number,
): Promise<string> => {
  const r = await axios.post<{ content: string }>(`${BASE}/training-pool/advisor`, {
    messages,
    customer_id: customerId ?? null,
  });
  return r.data.content;
};

export const submitNasInbox = async (items: NasSubmitItem[]): Promise<NasSubmitResponse> => {
  const r = await axios.post<NasSubmitResponse>(`${BASE}/training-pool/nas-inbox/submit`, { items });
  return r.data;
};

export const submitTrainingPool = async (
  files: File[],
  customerId: number | null,
  fileTypes: string[],
): Promise<TrainingPoolSubmitResponse> => {
  const form = new FormData();
  files.forEach(f => form.append('files', f));
  if (customerId !== null) form.append('customer_id', String(customerId));
  form.append('file_types', JSON.stringify(fileTypes));
  const r = await axios.post<TrainingPoolSubmitResponse>(`${BASE}/training-pool/submit`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return r.data;
};

// ─── Training Batches ─────────────────────────────────────────────────────────

export interface BatchSummary {
  diff_count: number;
  items_parsed: number;
  importer_tax_id: string | null;
}

export interface BatchFailure {
  file: string;
  reason: string;
  reason_zh: string;
}

export interface BatchStallInfo {
  message: string;
  accuracy_history: number[] | null;
  suggestion: string;
}

export interface TrainingBatch {
  batch_id: string;
  customer_id: number | null;
  customer_name: string | null;
  status: 'queued' | 'parsing' | 'analyzing' | 'completed' | 'failed' | 'stalled';
  file_count: number | null;
  file_names: string | null;
  error_message: string | null;
  sample_id: number | null;
  submitted_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  summary: BatchSummary | null;
  failures: BatchFailure[];
  stall_info: BatchStallInfo | null;
}

export const listTrainingBatches = async (
  status?: string,
  limit: number = 50,
): Promise<TrainingBatch[]> => {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  params.set('limit', String(limit));
  const r = await axios.get<TrainingBatch[]>(
    `${BASE}/training-pool/batches?${params.toString()}`
  );
  return r.data;
};
